import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { LanguageProvider } from './context/LanguageContext'
import Landing from './pages/Landing'
import Terms from './pages/Terms'
import Login from './pages/Login'
import Signup from './pages/Signup'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import AuthCallback from './pages/AuthCallback'
import Dashboard from './pages/Dashboard'
import LocationSetup from './pages/setup/LocationSetup'
import BrandingSetup from './pages/setup/BrandingSetup'
import TeeSheetSetup from './pages/setup/TeeSheetSetup'
import PackageManager from './pages/packages/PackageManager'
import EmbedCode from './pages/embed/EmbedCode'
import Analytics from './pages/Analytics'
import Bookings from './pages/Bookings'
import Reviews from './pages/Reviews'
import MyTrip from './pages/customer/MyTrip'
import CustomerSignup from './pages/customer/Signup'
import CustomerLogin from './pages/customer/Login'
import VerifyEmail from './pages/customer/VerifyEmail'
import MyBookings from './pages/customer/MyBookings'
import PayPage from './pages/customer/Pay'
import ReviewPage from './pages/customer/Review'
import SharedTrip from './pages/customer/SharedTrip'

// Reseller Platform v1 (Phase 1)
import AdminDashboard from './pages/admin/AdminDashboard'
import ResellerAccountsPage from './pages/admin/ResellerAccounts'
import ResellerAccountDetailPage from './pages/admin/ResellerAccountDetail'
import ResellerInvitesPage from './pages/admin/ResellerInvites'
import FeeSettingsPage from './pages/admin/FeeSettings'
import CourseIntegrationsPage from './pages/admin/CourseIntegrations'
import HotelIntegrationsPage from './pages/admin/HotelIntegrations'
import PlatformStatsPage from './pages/admin/PlatformStats'
import ResellerCoursesPage from './pages/admin/ResellerCourses'
import CourseIntegrationAddPage from './pages/admin/CourseIntegrationAdd'
import CourseIntegrationDetailPage from './pages/admin/CourseIntegrationDetail'
import DataMigrationPage from './pages/admin/DataMigration'
import ResellerDashboard from './pages/reseller/ResellerDashboard'
import BrandProfilePage from './pages/reseller/BrandProfile'
import EmailIntegrationPage from './pages/reseller/EmailIntegration'
import ConnectedCoursesPage from './pages/reseller/ConnectedCourses'
import PaymentSetupPage from './pages/reseller/PaymentSetup'
import GroupingsListPage from './pages/reseller/GroupingsList'
import ManagedAccountsPage from './pages/reseller/ManagedAccounts'
import GroupingEditPage from './pages/reseller/GroupingEdit'
import PackageEditPage from './pages/reseller/PackageEdit'
import GolfBundlesListPage from './pages/reseller/GolfBundlesList'
import BundleEditPage from './pages/reseller/BundleEdit'
import CustomTripsListPage from './pages/reseller/CustomTripsList'
import CustomTripBuilderPage from './pages/reseller/CustomTripBuilder'
import ResellerNetRateProperties from './pages/reseller/NetRateProperties'
import ResellerEmbed from './pages/embed/ResellerEmbed'
import EmbedErrorBoundary from './components/EmbedErrorBoundary'
import AutoResizeSearch from './pages/embed/AutoResizeSearch'
import EmbedTestHarness from './pages/embed/EmbedTestHarness'
import AutoResizeEmbed from './pages/embed/AutoResizeEmbed'
import CustomTripRequestEmbed from './pages/embed/CustomTripRequestEmbed'
import {
  CustomerMagicLanding,
  CustomerBundleMagic,
  CustomerLogin as ResellerCustomerLogin,
  CustomerTripsList,
  CustomerTripDetail,
  CustomerTripPay,
  CustomerReviewForm,
  CustomerQuotesList,
  CustomerPassesList,
  CustomerPassDetail,
  CustomerPassBook,
  CustomerGiftClaim,
  CustomerPayShare,
} from './pages/customer/ResellerCustomerPortal'
import CustomerQuoteView from './pages/CustomerQuoteView'
import PayShare from './pages/PayShare'
import {
  AccountProfile,
  AccountSecurity,
  AccountPreferences,
  AccountDanger,
} from './pages/customer/CustomerAccount'
import CustomDomainPage from './pages/reseller/CustomDomain'
import EmailHistoryPage from './pages/reseller/EmailHistory'
import BookingsPage, { BookingDetail as BookingDetailPage } from './pages/reseller/Bookings'
import NotificationSettingsPage from './pages/reseller/NotificationSettings'
import ReviewsPage from './pages/reseller/Reviews'
import MessagesInbox from './pages/reseller/MessagesInbox'
import ChangeRequests from './pages/reseller/ChangeRequests'
import ApiAccess from './pages/reseller/ApiAccess'
import ResellerTeam from './pages/reseller/ResellerTeam'
import ResellerPromoCodes from './pages/reseller/ResellerPromoCodes'
import ApiDocs from './pages/developers/ApiDocs'
import ResellerMyTrip from './pages/customer/ResellerMyTrip'
import { PayoutsList, PayoutsDetail } from './pages/admin/ResellerPayouts'
import ResellerSales from './pages/admin/ResellerSales'
import PayoutBatchesPage, { PayoutBatchDetail } from './pages/admin/PayoutBatches'
import AcceptResellerInvite from './pages/invite/AcceptResellerInvite'
import AcceptManagedInvite from './pages/invite/AcceptManagedInvite'
import TermsVersions from './pages/admin/TermsVersions'
import RefundQueue from './pages/admin/RefundQueue'
import AdminCoursePayments from './pages/admin/CoursePayments'
import AdminSplitQuotes from './pages/admin/SplitQuotes'
import AdminHotelAuthorizations from './pages/admin/HotelAuthorizations'
import MarginInspector from './pages/admin/MarginInspector'
import ResellerCoursePayments from './pages/reseller/CoursePayments'
import ResellerToBook from './pages/reseller/ToBookList'
import ResellerMarginInspector from './pages/reseller/MarginInspector'
import ChildTerms from './pages/reseller/ChildTerms'

function ProtectedRoute({ children }) {
  const { user } = useAuth()
  if (!user) return <Navigate to="/login" replace />
  return children
}

export default function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Landing />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/login" element={<Login />} />
        <Route path="/developers" element={<ApiDocs />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Portal — protected */}
        <Route path="/portal" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/portal/location" element={<ProtectedRoute><LocationSetup /></ProtectedRoute>} />
        <Route path="/portal/branding" element={<ProtectedRoute><BrandingSetup /></ProtectedRoute>} />
        <Route path="/portal/tee-sheet" element={<ProtectedRoute><TeeSheetSetup /></ProtectedRoute>} />
        <Route path="/portal/packages" element={<ProtectedRoute><PackageManager /></ProtectedRoute>} />
        <Route path="/portal/embed" element={<ProtectedRoute><EmbedCode /></ProtectedRoute>} />
        <Route path="/portal/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/portal/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
        <Route path="/portal/reviews" element={<ProtectedRoute><Reviews /></ProtectedRoute>} />

        {/* Customer-facing My-Trip portal (plan 450 Phase D/F) */}
        <Route path="/my-trip/:bookingId" element={<MyTrip />} />
        <Route path="/customer/signup" element={<CustomerSignup />} />
        <Route path="/customer/login" element={<CustomerLogin />} />
        <Route path="/customer/verify-email" element={<VerifyEmail />} />
        <Route path="/customer/dashboard" element={<MyBookings />} />
        <Route path="/pay/:token" element={<PayPage />} />
        <Route path="/quote/:token" element={<CustomerQuoteView />} />
        <Route path="/pay-share/:token" element={<PayShare />} />
        <Route path="/portal/reseller/custom-trips/:id/preview" element={<ProtectedRoute><CustomerQuoteView preview /></ProtectedRoute>} />
        <Route path="/review/:token" element={<ReviewPage />} />
        <Route path="/shared/:shareToken" element={<SharedTrip />} />

        {/* Reseller Platform v1 (Phase 1) — admin portal (super admin only) */}
        <Route path="/admin"                    element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/platform-stats"     element={<ProtectedRoute><PlatformStatsPage /></ProtectedRoute>} />
        <Route path="/admin/reseller-accounts"  element={<ProtectedRoute><ResellerAccountsPage /></ProtectedRoute>} />
        <Route path="/admin/reseller-sales"     element={<ProtectedRoute><ResellerSales /></ProtectedRoute>} />
        <Route path="/admin/reseller-accounts/:id" element={<ProtectedRoute><ResellerAccountDetailPage /></ProtectedRoute>} />
        <Route path="/admin/reseller-invites"   element={<ProtectedRoute><ResellerInvitesPage /></ProtectedRoute>} />
        <Route path="/admin/refund-queue"       element={<ProtectedRoute><RefundQueue /></ProtectedRoute>} />
        <Route path="/admin/course-payments"    element={<ProtectedRoute><AdminCoursePayments /></ProtectedRoute>} />
        <Route path="/admin/split-quotes"        element={<ProtectedRoute><AdminSplitQuotes /></ProtectedRoute>} />
        <Route path="/admin/hotel-authorizations" element={<ProtectedRoute><AdminHotelAuthorizations /></ProtectedRoute>} />
        <Route path="/admin/margin-inspector"   element={<ProtectedRoute><MarginInspector /></ProtectedRoute>} />
        <Route path="/admin/fee-settings"       element={<ProtectedRoute><FeeSettingsPage /></ProtectedRoute>} />
        <Route path="/admin/terms"              element={<ProtectedRoute><TermsVersions /></ProtectedRoute>} />
        <Route path="/admin/course-integrations" element={<ProtectedRoute><CourseIntegrationsPage /></ProtectedRoute>} />
        <Route path="/admin/course-integrations/add" element={<ProtectedRoute><CourseIntegrationAddPage /></ProtectedRoute>} />
        <Route path="/admin/course-integrations/:provider" element={<ProtectedRoute><CourseIntegrationDetailPage /></ProtectedRoute>} />
        <Route path="/admin/hotel-integrations" element={<ProtectedRoute><HotelIntegrationsPage /></ProtectedRoute>} />
        <Route path="/admin/reseller-courses" element={<ProtectedRoute><ResellerCoursesPage /></ProtectedRoute>} />
        <Route path="/admin/reseller-payouts"               element={<ProtectedRoute><PayoutsList /></ProtectedRoute>} />
        <Route path="/admin/reseller-payouts/:resellerId"   element={<ProtectedRoute><PayoutsDetail /></ProtectedRoute>} />
        <Route path="/admin/payouts"                        element={<ProtectedRoute><PayoutBatchesPage /></ProtectedRoute>} />
        <Route path="/admin/payouts/:id"                    element={<ProtectedRoute><PayoutBatchDetail /></ProtectedRoute>} />
        <Route path="/admin/data-migration"                 element={<ProtectedRoute><DataMigrationPage /></ProtectedRoute>} />

        {/* Reseller portal — Phase 1 dashboard + Phase 2 (brand + email) */}
        <Route path="/portal/reseller"                    element={<ProtectedRoute><ResellerDashboard /></ProtectedRoute>} />
        <Route path="/portal/reseller/brand"              element={<ProtectedRoute><BrandProfilePage /></ProtectedRoute>} />
        <Route path="/portal/reseller/email-integration"  element={<ProtectedRoute><EmailIntegrationPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/courses"             element={<ProtectedRoute><ConnectedCoursesPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/course-payments"     element={<ProtectedRoute><ResellerCoursePayments /></ProtectedRoute>} />
        <Route path="/portal/reseller/to-book"             element={<ProtectedRoute><ResellerToBook /></ProtectedRoute>} />
        <Route path="/portal/reseller/margin-inspector"    element={<ProtectedRoute><ResellerMarginInspector /></ProtectedRoute>} />
        <Route path="/portal/reseller/payment"             element={<ProtectedRoute><PaymentSetupPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/api-access"          element={<ProtectedRoute><ApiAccess /></ProtectedRoute>} />
        <Route path="/portal/reseller/team"                element={<ProtectedRoute><ResellerTeam /></ProtectedRoute>} />
        <Route path="/portal/reseller/promo-codes"         element={<ProtectedRoute><ResellerPromoCodes /></ProtectedRoute>} />
        <Route path="/portal/reseller/groupings"           element={<ProtectedRoute><GroupingsListPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/managed-accounts"    element={<ProtectedRoute><ManagedAccountsPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/child-terms"         element={<ProtectedRoute><ChildTerms /></ProtectedRoute>} />
        <Route path="/portal/reseller/groupings/:id"       element={<ProtectedRoute><GroupingEditPage /></ProtectedRoute>} />
        {/* Standalone packages list deprecated — packages live under each grouping now.
            The /:id editor stays since it's the canonical edit URL. */}
        <Route path="/portal/reseller/packages"            element={<Navigate to="/portal/reseller/groupings" replace />} />
        <Route path="/portal/reseller/packages/:id"        element={<ProtectedRoute><PackageEditPage /></ProtectedRoute>} />
        {/* Golf Bundles (EPIC #116) — prepaid rounds, separate from Stay & Play. */}
        <Route path="/portal/reseller/golf-bundles"        element={<ProtectedRoute><GolfBundlesListPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/golf-bundles/new"    element={<ProtectedRoute><BundleEditPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/golf-bundles/:id"    element={<ProtectedRoute><BundleEditPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/custom-trips"        element={<ProtectedRoute><CustomTripsListPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/custom-trips/:id"    element={<ProtectedRoute><CustomTripBuilderPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/net-rate-properties" element={<ProtectedRoute><ResellerNetRateProperties /></ProtectedRoute>} />
        <Route path="/portal/reseller/domain"              element={<ProtectedRoute><CustomDomainPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/email-history"       element={<ProtectedRoute><EmailHistoryPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/bookings"            element={<ProtectedRoute><BookingsPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/bookings/:id"        element={<ProtectedRoute><BookingDetailPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/notifications"       element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/reviews"             element={<ProtectedRoute><ReviewsPage /></ProtectedRoute>} />
        <Route path="/portal/reseller/messages"            element={<ProtectedRoute><MessagesInbox /></ProtectedRoute>} />
        <Route path="/portal/reseller/messages/:bookingId" element={<ProtectedRoute><MessagesInbox /></ProtectedRoute>} />
        <Route path="/portal/reseller/change-requests"     element={<ProtectedRoute><ChangeRequests /></ProtectedRoute>} />
        <Route path="/portal/reseller/change-requests/:id" element={<ProtectedRoute><ChangeRequests /></ProtectedRoute>} />

        {/* Customer-facing reseller my-trip portal (Phase 10) — magic-link auth */}
        <Route path="/reseller-trip/:bookingId" element={<ResellerMyTrip />} />

        {/* Public magic-link invite acceptance (no auth — token in URL is the credential) */}
        <Route path="/invite/reseller/:token"   element={<AcceptResellerInvite />} />
        <Route path="/managed-invite/accept"     element={<AcceptManagedInvite />} />

        {/* Public customer-facing reseller iframe (Phase 7) — no auth, iframeKey is the credential
            Wrapped in EmbedErrorBoundary so a single render-time exception shows an actionable
            error UI instead of unmounting the tree and leaving a blank iframe. */}
        {/* Standard embed — AutoResizeEmbed wraps the widget with the height
            handshake so the host iframe resizes to fit its content. */}
        <Route path="/embed/reseller/:iframeKey" element={<EmbedErrorBoundary><AutoResizeEmbed /></EmbedErrorBoundary>} />
        <Route path="/embed/custom-trip/:key" element={<EmbedErrorBoundary><CustomTripRequestEmbed /></EmbedErrorBoundary>} />

        {/* Public account-wide search iframe (Phase 9) — auto-resizing wrapper
            so the host iframe fits its content. Existing fixed-height embeds are
            unaffected (they ignore the resize message). */}
        <Route path="/embed/reseller-search/:accountSlug" element={<EmbedErrorBoundary><AutoResizeSearch /></EmbedErrorBoundary>} />

        {/* Legacy alias — kept working (points at the same auto-resize wrapper)
            for any embeds pasted during the mobile-resize trial. */}
        <Route path="/embed/reseller-test/:iframeKey" element={<EmbedErrorBoundary><AutoResizeEmbed /></EmbedErrorBoundary>} />

        {/* Dev/testing harness — simulates a partner page hosting the test
            iframe so we can see it auto-resize on a real device. Does not touch
            the live embed. Load: /embed-test?key=<grouping IframeKey> */}
        <Route path="/embed-test" element={<EmbedTestHarness />} />

        {/* Pass D step 9 — reseller customer portal. Lives under
            /r/:slug/customer/* to avoid colliding with the Stay & Play
            customer portal at /customer/*. Custom-domain routing
            (step 10) maps a reseller's CNAME to /r/{their-slug}/. */}
        <Route path="/r/:resellerSlug/customer/magic"     element={<CustomerMagicLanding />} />
        <Route path="/r/:resellerSlug/customer/magic-bundle" element={<CustomerBundleMagic />} />
        <Route path="/r/:resellerSlug/customer/book"      element={<CustomerPassBook />} />
        <Route path="/r/:resellerSlug/customer/login"     element={<ResellerCustomerLogin />} />
        <Route path="/r/:resellerSlug/customer/trips"     element={<CustomerTripsList />} />
        <Route path="/r/:resellerSlug/customer/trips/:id" element={<CustomerTripDetail />} />
        <Route path="/r/:resellerSlug/customer/trips/:id/pay" element={<CustomerTripPay />} />
        <Route path="/r/:resellerSlug/customer/trips/:id/review" element={<CustomerReviewForm />} />
        <Route path="/r/:resellerSlug/customer/quotes"     element={<CustomerQuotesList />} />
        <Route path="/r/:resellerSlug/customer/passes"     element={<CustomerPassesList />} />
        <Route path="/r/:resellerSlug/customer/passes/:id" element={<CustomerPassDetail />} />
        <Route path="/r/:resellerSlug/customer/passes/:id/book" element={<CustomerPassBook />} />
        <Route path="/r/:resellerSlug/customer/claim/:token"    element={<CustomerGiftClaim />} />
        <Route path="/r/:resellerSlug/embed/reseller-pay-share" element={<CustomerPayShare />} />
        <Route path="/r/:resellerSlug/customer/account/profile"     element={<AccountProfile />} />
        <Route path="/r/:resellerSlug/customer/account/security"    element={<AccountSecurity />} />
        <Route path="/r/:resellerSlug/customer/account/preferences" element={<AccountPreferences />} />
        <Route path="/r/:resellerSlug/customer/account/danger"      element={<AccountDanger />} />

        {/* Bare reseller-customer routes — used when the SPA loads on a
            reseller's custom domain (Phase 8 host-based routing). No
            /r/:slug/ prefix in the URL; useResellerBrand falls back to
            /brand-by-host using window.location.hostname.

            /customer/login is NOT mounted here because it collides with
            the Stay & Play customer login (same path, different product).
            Magic-link is the primary entry on custom domains; password
            login can still be reached via /r/{slug}/customer/login. */}
        <Route path="/customer/magic"             element={<CustomerMagicLanding />} />
        <Route path="/customer/magic-bundle"      element={<CustomerBundleMagic />} />
        <Route path="/customer/book"              element={<CustomerPassBook />} />
        <Route path="/customer/trips"             element={<CustomerTripsList />} />
        <Route path="/customer/trips/:id"         element={<CustomerTripDetail />} />
        <Route path="/customer/trips/:id/pay"     element={<CustomerTripPay />} />
        <Route path="/customer/trips/:id/review"  element={<CustomerReviewForm />} />
        <Route path="/customer/quotes"            element={<CustomerQuotesList />} />
        <Route path="/customer/passes"            element={<CustomerPassesList />} />
        <Route path="/customer/passes/:id"        element={<CustomerPassDetail />} />
        <Route path="/customer/passes/:id/book"   element={<CustomerPassBook />} />
        <Route path="/customer/claim/:token"      element={<CustomerGiftClaim />} />
        <Route path="/embed/reseller-pay-share"   element={<CustomerPayShare />} />
        <Route path="/customer/account/profile"     element={<AccountProfile />} />
        <Route path="/customer/account/security"    element={<AccountSecurity />} />
        <Route path="/customer/account/preferences" element={<AccountPreferences />} />
        <Route path="/customer/account/danger"      element={<AccountDanger />} />
        {/* Reseller customer-portal login on a custom domain. Lives at
            /customer/sign-in (not /customer/login) because the latter is
            already claimed by Stay & Play. useScopedPath('login') emits
            this path automatically in host mode. */}
        <Route path="/customer/sign-in"           element={<ResellerCustomerLogin />} />

        {/* Legacy — redirect old onboarding route to dashboard */}
        <Route path="/onboarding" element={<Navigate to="/portal" replace />} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </LanguageProvider>
    </AuthProvider>
  )
}
